import React, { useEffect, useState } from "react";
import { Loader2, TrendingUp, Award, Percent, MousePointerClick, IdCard, BadgeCheck, Target, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const KPIS = [
    { key: "conversion_rate_pct", Icon: Percent, label: "Conversion Rate", suffix: "%" },
    { key: "invitation_acceptance_pct", Icon: MousePointerClick, label: "Invitation Acceptance", suffix: "%" },
    { key: "passport_completion_rate_pct", Icon: IdCard, label: "Passport Completion", suffix: "%" },
    { key: "verification_completion_rate_pct", Icon: BadgeCheck, label: "Verification Rate", suffix: "%" },
    { key: "employer_hire_rate_pct", Icon: Target, label: "Employer Hire Rate", suffix: "%" },
    { key: "avg_days_to_hire", Icon: Clock, label: "Avg Days to Hire", suffix: "d" },
];

export default function RecruitAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/admin/recruit/analytics")
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);
    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>;
    const k = data?.kpis || {};
    return (
        <div data-testid="recruit-analytics" className="space-y-4">
            <div>
                <h2 className="font-heading font-medium text-lg">Analytics</h2>
                <p className="text-sm text-muted-foreground">Every KPI across every search, campaign, and candidate.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {KPIS.map((k1) => (
                    <div key={k1.key} className="card-soft p-4" data-testid={`analytics-kpi-${k1.key}`}>
                        <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                            <k1.Icon size={11} /> {k1.label}
                        </div>
                        <div className="mt-1 font-heading font-bold text-2xl mono-score">
                            {k[k1.key] ?? 0}{k1.suffix && <span className="text-sm text-muted-foreground ml-0.5">{k1.suffix}</span>}
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="card-soft p-4" data-testid="analytics-top-campaigns">
                    <h3 className="font-heading font-medium mb-3">Top campaigns</h3>
                    {(data?.top_campaigns || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">No campaigns yet.</div>
                    ) : (
                        <div className="divide-y divide-border">
                            {data.top_campaigns.map((c) => (
                                <div key={c.id} className="py-2 flex items-center justify-between text-sm">
                                    <span className="truncate">{c.name}</span>
                                    <span className="text-xs text-muted-foreground shrink-0 ml-2">{c.passports_started || 0} passports · {c.clicks || 0} clicks · {c.prospects || 0} prospects</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="card-soft p-4" data-testid="analytics-top-sources">
                    <h3 className="font-heading font-medium mb-3">Top recruiting sources</h3>
                    {(data?.top_sources || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">No candidates yet.</div>
                    ) : (
                        <div className="divide-y divide-border">
                            {data.top_sources.map((s) => (
                                <div key={s.source} className="py-2 flex items-center justify-between text-sm">
                                    <span className="capitalize">{s.source}</span>
                                    <span className="font-mono text-muted-foreground">{s.count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="card-soft p-4" data-testid="analytics-funnel">
                <h3 className="font-heading font-medium mb-3">Funnel</h3>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-2 text-center">
                    {["invited", "opened", "clicked", "passport_started", "completed", "verified", "placed"].map((step) => (
                        <div key={step} className="rounded-md bg-muted/40 p-2">
                            <div className="font-heading font-bold text-lg mono-score">{data?.funnel?.[step] ?? 0}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{step.replace(/_/g, " ")}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
